import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { StickyContainer, Sticky } from 'react-sticky';
import _throttle from 'lodash/throttle';
import _isEqual from 'lodash/isEqual';
import ScrollUpArrow from './ScrollUpArrow';

/**
 * StickyHeaderTable
 * Both header and table must be class based components so that we can make references to them
 * @prop {react component} StickyHeader - thead.
 * @prop {react component} StaticTable - table wrapping the StickyHeader component and a tbody.
 * @prop {string} dataPropName - The name of the table data prop.
 * @example
 *  // returns StickyHeaderTable
 *  class CustomHeader extends Component {
 *    render() {
 *      const { style } = this.props;
 *      return (
 *        <thead style={style}>
 *          <tr><th>Name</th></tr>
 *        </thead>
 *      );
 *    }
 *  }
 *  CustomHeader.defaults = {
 *    style: {}
 *  };
 *  CustomHeader.propTypes = {
 *    style: PropTypes.objectOf(PropTypes.string)
 *  };
 *  // 'names' prop can be named anything and is an object that represents
 *  // the table data. When the object changes the table header resizes to match
 *  // the new data.
 *  class CustomTable extends Component {
 *    render() {
 *      const { headerStyle, names } = this.props;
 *      const nameEls = names.map(name => (<tr><td>{name}</td></tr>));
 *      return (
 *        <table>
 *          <CustomHeader style={headerStyle}/>
 *          <tbody>{nameEls}</tbody>
 *        </table>
 *      );
 *    }
 *  }
 *  CustomTable.defaults = {
 *    headerStyle: {},
 *    names: ['bob']
 *  };
 *  CustomTable.propTypes = {
 *    headerStyle: PropTypes.objectOf(PropTypes.string),
 *    names: PropTypes.arrayOf(PropTypes.string)
 *  }
 *
 *  <StickyHeaderTable
 *    StickyHeader={CustomHeader}
 *    StaticTable={CustomTable}
 *    dataPropName: 'names'
 *  />
 */

class StickyHeaderTable extends Component {
  constructor(props) {
    super(props);
    this.stickyStateUpdate = this.stickyStateUpdate.bind(this);
    this.onResize = _throttle(this.onResize.bind(this), 20);
    this.onScroll = _throttle(this.onScroll.bind(this), 200);
    this.state = {
      headerNeedsResize: true,
      headerHeight: 70,
      headerSticky: false,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.onResize);
    window.addEventListener('scroll', this.onScroll);
  }

  componentWillReceiveProps(nextProps) {
    const { dataPropName } = this.props;
    const data = nextProps[dataPropName];
    let headerNeedsResize = false;

    if (!_isEqual(data, this.props[dataPropName])) {
      headerNeedsResize = true;
    } else if (this.props.activeEventTab !== nextProps.activeEventTab) { // eslint-disable-line
      headerNeedsResize = true;
    }

    this.setState({
      headerNeedsResize
    });
  }

  componentDidUpdate() {
    const { headerNeedsResize } = this.state;

    if (headerNeedsResize) {
      this.onResize();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
  }

  onResize() {
    const headerEl = ReactDOM.findDOMNode(this.header); // eslint-disable-line
    const thead = headerEl && headerEl.querySelector('thead'); // eslint-disable-line
    const tableEl = ReactDOM.findDOMNode(this.table); // eslint-disable-line
    const tr = tableEl && tableEl.querySelector('tbody tr'); // eslint-disable-line

    if (tr) {
      const tds = [...tr.querySelectorAll('td')];
      const ths = [...thead.querySelectorAll('th')];
      const columnWidths = tds.map(td => parseFloat(td.getBoundingClientRect().width, 10));
      columnWidths.forEach((width, index) => {
        ths[index].style.width = `${width}px`;
      });
    }
    if (!tableEl || !thead) return; // don't go further if either tag is missing

    const tableBoundingBox = tableEl.getBoundingClientRect();
    const headerBoundingBox = thead.getBoundingClientRect();
    const headerHeight = parseFloat(headerBoundingBox.height, 10);
    const tableWidth = parseFloat(tableBoundingBox.width, 10);
    this.setState({ headerHeight, tableWidth, headerNeedsResize: false });
  }

  onScroll() {
    const table = ReactDOM.findDOMNode(this.table); // eslint-disable-line
    if (!table) return;
    const tableBoundingBox = table.getBoundingClientRect();
    const tableWidth = parseFloat(tableBoundingBox.width, 10);
    this.setState({ tableWidth });
  }

  stickyStateUpdate(sticky) {
    this.setState({ headerSticky: sticky });
  }

  render() {
    const { stickyHeaderCls, noStickyContainer, StickyHeader, StaticTable } = this.props;
    const { headerHeight, tableWidth, headerSticky } = this.state;

    const stickyStyle = {
      height: headerHeight,
      zIndex: 1,
      // opacity: `${headerSticky ? '1' : '0'}`,
    };
    const tableHeaderStyle = {
      height: `${headerHeight}px`,
      // opacity: `${headerSticky ? '0' : '1'}`,
    };

    const { table } = this;

    const tableId = 'static-table-ref-id';
    const Container = noStickyContainer ? 'div' : StickyContainer;
    return (
      <Container>
        <ScrollUpArrow
          isSticky={headerSticky}
          container={table}
          tableId={tableId}
        />
        <div>
          <div className="visible-xs">
            <Sticky
              onStickyStateChange={this.stickyStateUpdate}
              stickyStyle={{ top: 50, width: tableWidth }}
              style={stickyStyle}
            >
              <table
                className={`${stickyHeaderCls} table-fixed-header-${headerSticky ? 'square' : 'round'}`}
                ref={(c) => { this.header = c; }}
              >
                <StickyHeader {...this.props} headerSticky={headerSticky} />
              </table>
            </Sticky>
          </div>
          <div className="hidden-xs">
            <Sticky
              onStickyStateChange={this.stickyStateUpdate}
              stickyStyle={{ width: tableWidth, top: 105 }}
              style={stickyStyle}
              topOffset={-74}
            >
              <table
                className={`${stickyHeaderCls} table-fixed-header-${headerSticky ? 'square' : 'round'}`}
                ref={(c) => { this.header = c; }}
              >
                <StickyHeader {...this.props} headerSticky={headerSticky} />
              </table>
            </Sticky>
          </div>
        </div>
        <div style={{ marginTop: -headerHeight }}>
          <StaticTable {...this.props} ref={(c) => { this.table = c; }} tableId={tableId} headerStyle={tableHeaderStyle} />
        </div>
      </Container>
    );
  }
}

StickyHeaderTable.defaults = {
  stickyHeaderCls: '',
  noStickyContainer: false
};

StickyHeaderTable.propTypes = {
  stickyHeaderCls: PropTypes.string,
  noStickyContainer: PropTypes.bool,
  StickyHeader: PropTypes.func.isRequired,
  StaticTable: PropTypes.func.isRequired,
  dataPropName: PropTypes.string.isRequired,
};

export default StickyHeaderTable;



// WEBPACK FOOTER //
// ./src/components/common/ui/StickyHeaderTable.jsx